/**
 * Rules behind a play correction, an inserted play and a deletion: who may
 * write, which fields the dialog offers for the play's phase (from the column
 * registry — never a hand-kept list), what a draft turns into on the wire, and
 * what the browser rejects before the server has to (AC-ARCH-001 §27,
 * AC-RBAC-003).
 */
import type { PlayPatch, PlayView } from '../../lib/api-types';
import type { Persona } from '../../lib/personas';
import {
  CLOCK_PATTERN,
  DEFENSIVE_TD_CAUSE_MESSAGE,
  FIELD_BOUNDS,
  ILLEGAL_SPECIALTY_PAIRS,
  PHASE_LABELS,
  PLAY_TYPES_BY_PHASE,
  defaultPlayType,
  isAcceptedOutcome,
  isDefensiveTouchdownWithoutCause,
  isPlayPhase,
  isScrimmagePlay,
  type PlayPhase,
} from '../play-entry/play-catalog';

import {
  EDIT_SECTIONS,
  editSectionFor,
  num,
  phaseOf,
  type ArchiveColumn,
  type EditField,
  type EditSection,
} from './archive-columns';

/* ------------------------------------------------------------ permissions */

/** `PATCH` / insert while the event is live — mirrors plays-service `PLAY_WRITE_PERSONAS`. */
const PLAY_EDIT_PERSONAS: readonly Persona[] = [
  'primary_stat_operator',
  'general_stat_operator',
  'offensive_coordinator',
  'defensive_coordinator',
  'coach',
  'team_admin',
];

/** `DELETE /plays/v1/{id}` is STAFF in the gateway RBAC table. */
const PLAY_DELETE_PERSONAS: readonly Persona[] = [
  'team_admin',
  'offensive_coordinator',
  'defensive_coordinator',
  'coach',
];

export interface EditPermission {
  canEdit: boolean;
  /** Required post-close: the audited "why" behind the correction. */
  isReasonRequired: boolean;
  /** Why editing is blocked, phrased for the person reading it. */
  deniedReason: string | null;
}

/**
 * AC-RBAC-003 as the browser sees it. While a game is live everyone keeping
 * stats can fix a typo; once it closes only a Team Admin may, with a reason.
 */
export function resolveEditPermission(
  persona: Persona | null,
  eventStatus: string
): EditPermission {
  if (persona === null) {
    return { canEdit: false, isReasonRequired: false, deniedReason: 'Sign in to edit plays.' };
  }
  if (eventStatus === 'closed') {
    return persona === 'team_admin'
      ? { canEdit: true, isReasonRequired: true, deniedReason: null }
      : {
          canEdit: false,
          isReasonRequired: true,
          deniedReason: 'Event is closed. Only Team Admin can edit.',
        };
  }
  return PLAY_EDIT_PERSONAS.includes(persona)
    ? { canEdit: true, isReasonRequired: false, deniedReason: null }
    : {
        canEdit: false,
        isReasonRequired: false,
        deniedReason: 'Your role can view plays but not change them.',
      };
}

/** Deleting is coaching staff only (stat operators cannot), Team Admin post-close. */
export function resolveDeletePermission(
  persona: Persona | null,
  eventStatus: string
): EditPermission {
  if (persona === null) {
    return { canEdit: false, isReasonRequired: false, deniedReason: 'Sign in to delete plays.' };
  }
  if (eventStatus === 'closed') {
    return persona === 'team_admin'
      ? { canEdit: true, isReasonRequired: true, deniedReason: null }
      : {
          canEdit: false,
          isReasonRequired: true,
          deniedReason: 'Event is closed. Only Team Admin can delete plays.',
        };
  }
  return PLAY_DELETE_PERSONAS.includes(persona)
    ? { canEdit: true, isReasonRequired: false, deniedReason: null }
    : {
        canEdit: false,
        isReasonRequired: false,
        deniedReason: 'Only coaches and the Team Admin can delete plays.',
      };
}

/* ----------------------------------------------------------------- fields */

export type Draft = Record<string, string>;

export interface ResolvedField {
  field: EditField;
  column: ArchiveColumn;
  section: EditSection;
}

export interface FieldSection {
  id: EditSection;
  label: string;
  fields: ResolvedField[];
}

/**
 * The dialog for one phase: every registry edit field that applies to the
 * phase, grouped into sections in registry order. Retired custom fields are
 * offered only when the play already carries a value for them.
 */
export function editSectionsFor(
  columns: readonly ArchiveColumn[],
  phase: PlayPhase,
  draft: Readonly<Draft>
): FieldSection[] {
  const resolved: ResolvedField[] = [];
  const seen = new Set<string>();
  for (const column of columns) {
    for (const field of column.editFields) {
      if (!field.phases.includes(phase) || seen.has(field.name)) continue;
      if (column.customField !== undefined && !column.customField.is_enabled) {
        if ((draft[field.name] ?? '') === '') continue;
      }
      seen.add(field.name);
      resolved.push({ field, column, section: editSectionFor(column, field, phase) });
    }
  }
  return EDIT_SECTIONS.map((section) => ({
    id: section.id,
    label: section.label,
    fields: resolved.filter((entry) => entry.section === section.id),
  })).filter((section) => section.fields.length > 0);
}

/** Every edit field across all phases (unique by name). */
function allEditFields(columns: readonly ArchiveColumn[]): EditField[] {
  const out = new Map<string, EditField>();
  for (const column of columns) for (const field of column.editFields) out.set(field.name, field);
  return [...out.values()];
}

/** Any stat value → the string a control holds. */
export function toInputValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(String).join(', ');
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

function customBag(play: PlayView): Record<string, unknown> {
  const fromView = (play as { custom_stats?: unknown }).custom_stats;
  const nested = play.stats.custom_stats;
  const pick = (value: unknown): Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  return { ...pick(nested), ...pick(fromView) };
}

/** Option values of every `storedInTags` field (the penalty chips). */
function tagChipValues(columns: readonly ArchiveColumn[]): Map<string, readonly string[]> {
  const out = new Map<string, readonly string[]>();
  for (const field of allEditFields(columns)) {
    if (field.storedInTags !== true) continue;
    out.set(
      field.name,
      (field.options?.('offense', {}) ?? []).map((option) => option.value.toLowerCase())
    );
  }
  return out;
}

/** Seed the draft from a play's active version. */
export function draftFromPlay(play: PlayView, columns: readonly ArchiveColumn[]): Draft {
  const custom = customBag(play);
  const draft: Draft = {};
  const chips = tagChipValues(columns);
  const allChips = new Set([...chips.values()].flat());
  const tags = Array.isArray(play.stats.tags) ? play.stats.tags.map(String) : [];
  for (const field of allEditFields(columns)) {
    if (field.storedInTags === true) {
      // Penalty chips pre-selected from the play's tags.
      const values = chips.get(field.name) ?? [];
      draft[field.name] = toInputValue(
        tags.filter((tag) => values.includes(tag.toLowerCase())).map((tag) => tag.toLowerCase())
      );
      continue;
    }
    if (field.name === 'tags' && allChips.size > 0) {
      draft.tags = toInputValue(tags.filter((tag) => !allChips.has(tag.toLowerCase())));
      continue;
    }
    draft[field.name] = toInputValue(
      field.customKey === undefined ? play.stats[field.name] : custom[field.customKey]
    );
  }
  return draft;
}

/** The `tags[]` a draft posts: the chip fields' values first, then the free tags. */
function mergedTags(
  columns: readonly ArchiveColumn[],
  draft: Readonly<Draft>,
  phase: PlayPhase
): string[] {
  const out: string[] = [];
  for (const field of allEditFields(columns)) {
    if (field.storedInTags !== true || !field.phases.includes(phase)) continue;
    out.push(...listOf(draft[field.name] ?? ''));
  }
  for (const tag of listOf(draft.tags ?? '')) {
    if (!out.some((existing) => existing.toLowerCase() === tag.toLowerCase())) out.push(tag);
  }
  return out;
}

function hasTagChips(columns: readonly ArchiveColumn[]): boolean {
  return allEditFields(columns).some((field) => field.storedInTags === true);
}

function listOf(raw: string): string[] {
  return raw
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');
}

function typed(field: EditField, raw: string): unknown {
  const trimmed = raw.trim();
  if (field.kind === 'number') return trimmed === '' ? null : Number(trimmed);
  if (field.kind === 'multi' || field.kind === 'list') return listOf(raw);
  if (field.name === 'quarter' || field.name === 'down') {
    return trimmed === '' ? null : Number(trimmed);
  }
  if (field.customKey !== undefined && trimmed === 'true') return true;
  if (field.customKey !== undefined && trimmed === 'false') return false;
  return trimmed === '' ? null : trimmed;
}

function phaseFromDraft(draft: Readonly<Draft>, fallback: PlayPhase): PlayPhase {
  const value = draft.possession;
  return isPlayPhase(value) ? value : fallback;
}

/**
 * Draft → PATCH body with only what changed. When the unit changes, fields
 * that do not exist on the new unit are cleared so the previous phase's call
 * never rides along (same rule as Play Entry's payload).
 */
export function buildPatch(
  play: PlayView,
  columns: readonly ArchiveColumn[],
  draft: Readonly<Draft>
): PlayPatch {
  const original = draftFromPlay(play, columns);
  const originalPhase = phaseOf(play) ?? 'offense';
  const phase = phaseFromDraft(draft, originalPhase);
  const patch: PlayPatch = {};
  const custom: Record<string, unknown> = {};
  let isCustomChanged = false;
  const chipped = hasTagChips(columns);
  for (const field of allEditFields(columns)) {
    // Penalty chips + free tags are merged into one `tags` array below.
    if (field.storedInTags === true || (chipped && field.name === 'tags')) continue;
    const isOnPhase = field.phases.includes(phase);
    const next = isOnPhase ? (draft[field.name] ?? '') : '';
    if (field.customKey !== undefined) {
      if (next !== (original[field.name] ?? '')) isCustomChanged = true;
      const value = typed(field, next);
      const isEmpty = value === null || (Array.isArray(value) && value.length === 0);
      if (!isEmpty) custom[field.customKey] = value;
      continue;
    }
    if (next === (original[field.name] ?? '')) continue;
    patch[field.name] = typed(field, next);
  }
  if (chipped) {
    const before = mergedTags(columns, original, originalPhase);
    const after = mergedTags(columns, draft, phase);
    if (before.join('\u0000') !== after.join('\u0000')) patch.tags = after;
  }
  if (isCustomChanged) {
    // Keep values of custom stats the dialog does not show (retired, other unit).
    const merged = { ...customBag(play) };
    for (const field of allEditFields(columns)) {
      if (field.customKey !== undefined) delete merged[field.customKey];
    }
    patch.custom_stats = { ...merged, ...custom };
  }
  if (phase !== originalPhase) {
    // Blitz booleans are derived server-side from `blitz_call`.
    patch.opponent_blitz = null;
    patch.defensive_blitz = null;
  }
  return patch;
}

/**
 * Draft → `POST /plays/v1/events/{id}/insert` body: the same fields Play
 * Entry posts (every catalog field — this phase's value or null), plus the
 * position the play takes.
 */
export function buildInsertPayload(
  eventId: string,
  columns: readonly ArchiveColumn[],
  draft: Readonly<Draft>,
  position: number
): Record<string, unknown> & { position: number } {
  const phase = phaseFromDraft(draft, 'offense');
  const payload: Record<string, unknown> = {
    event_id: eventId,
    client_timestamp: new Date().toISOString(),
  };
  const custom: Record<string, unknown> = {};
  const chipped = hasTagChips(columns);
  for (const field of allEditFields(columns)) {
    if (field.storedInTags === true || (chipped && field.name === 'tags')) continue;
    const isOnPhase = field.phases.includes(phase);
    const value = typed(field, isOnPhase ? (draft[field.name] ?? '') : '');
    if (field.customKey !== undefined) {
      const isEmpty = value === null || (Array.isArray(value) && value.length === 0);
      if (isOnPhase && !isEmpty) custom[field.customKey] = value;
      continue;
    }
    payload[field.name] = value;
  }
  if (chipped) payload.tags = mergedTags(columns, draft, phase);
  payload.possession = phase;
  payload.play_outcome = payload.play_outcome ?? 'na';
  payload.opponent_blitz = null;
  payload.defensive_blitz = null;
  if (Object.keys(custom).length > 0) payload.custom_stats = custom;
  return { ...payload, position };
}

/* ----------------------------------------------------------------- insert */

export type InsertSide = 'before' | 'after';

/** Where an inserted play lands: before #N → position N; after #N → N + 1. */
export function insertPosition(anchor: PlayView, side: InsertSide): number {
  return side === 'before' ? anchor.play_number : anchor.play_number + 1;
}

/**
 * A sensible starting point for a missed play: the unit, quarter and clock of
 * the play before the gap; down, spot and chains continued from it when the
 * same unit kept the ball (else taken from the play after the gap).
 */
export function seedInsertDraft(
  columns: readonly ArchiveColumn[],
  previous: PlayView | null,
  next: PlayView | null
): Draft {
  const draft: Draft = {};
  for (const field of allEditFields(columns)) draft[field.name] = '';
  const source = previous ?? next;
  const phase: PlayPhase = (source === null ? null : phaseOf(source)) ?? 'offense';
  draft.possession = phase;
  draft.play_type = defaultPlayType(phase);
  draft.play_outcome = 'na';
  const from = (play: PlayView | null, key: string): string => toInputValue(play?.stats[key]);
  draft.quarter = from(source, 'quarter') || '1';
  draft.game_clock = from(previous, 'game_clock');
  draft.hash = '';

  if (previous !== null && phase !== 'special_teams') {
    const down = num(previous.stats.down);
    const los = num(previous.stats.line_of_scrimmage);
    const gain = num(previous.stats.gain_loss) ?? 0;
    const marker = num(previous.stats.first_down_line);
    const spot = los === null ? null : Math.min(99, Math.max(1, los + gain));
    const isFirstDown = spot !== null && marker !== null && spot >= marker;
    draft.down = isFirstDown ? '1' : down === null ? '' : String(Math.min(4, down + 1));
    draft.line_of_scrimmage = spot === null ? '' : String(spot);
    draft.first_down_line = isFirstDown
      ? String(Math.min(99, (spot ?? 0) + FIELD_BOUNDS.yardsToGo))
      : marker === null
        ? ''
        : String(marker);
  } else if (next !== null) {
    draft.down = from(next, 'down');
    draft.line_of_scrimmage = from(next, 'line_of_scrimmage');
    draft.first_down_line = from(next, 'first_down_line');
  }
  if (phase === 'special_teams') draft.down = '';
  return draft;
}

/* ------------------------------------------------------------- validation */

function checkRange(
  errors: Record<string, string>,
  draft: Readonly<Draft>,
  field: string,
  label: string,
  min: number,
  max: number,
  isRequired = false
): void {
  const raw = (draft[field] ?? '').trim();
  if (raw === '') {
    if (isRequired) errors[field] = `${label} is required.`;
    return;
  }
  if (!/^-?\d+$/.test(raw)) {
    errors[field] = `${label} must be a whole number.`;
    return;
  }
  const value = Number(raw);
  if (value < min || value > max) errors[field] = `${label} must be between ${min} and ${max}.`;
}

/** Client-side guards that mirror plays-service's own field rules. */
export function validateDraft(draft: Readonly<Draft>): Record<string, string> {
  const errors: Record<string, string> = {};
  const phase = phaseFromDraft(draft, 'offense');
  const quarter = (draft.quarter ?? '').trim();
  if (quarter !== '' && !/^[1-8]$/.test(quarter)) {
    errors.quarter = `Quarter must be 1–${FIELD_BOUNDS.quarterMax}. Received: ${quarter}`;
  }
  const down = (draft.down ?? '').trim();
  if (down !== '' && !/^[1-4]$/.test(down)) errors.down = `Down must be 1–4. Received: ${down}`;
  const clock = (draft.game_clock ?? '').trim();
  if (clock !== '') {
    const match = CLOCK_PATTERN.exec(clock);
    if (match === null) errors.game_clock = 'Game clock must look like 07:32.';
    else if (Number(match[2]) > 59) errors.game_clock = 'Seconds must be 00–59.';
    else if (Number(match[1]) > FIELD_BOUNDS.clockMaxMinutes) {
      errors.game_clock = `Game clock must be between 00:00 and ${FIELD_BOUNDS.clockMaxMinutes}:00.`;
    }
  }
  const playType = draft.play_type ?? '';
  const isScrimmage = isScrimmagePlay(playType);
  checkRange(errors, draft, 'gain_loss', 'Gain / loss', -99, 99, isScrimmage);
  if (errors.gain_loss === 'Gain / loss must be a whole number.') {
    errors.gain_loss = 'Gain / loss must be a whole number of yards.';
  }
  if (isScrimmage && down === '') errors.down = 'Down is required on a scrimmage play.';
  checkRange(errors, draft, 'line_of_scrimmage', 'Line of scrimmage', 1, 99);
  checkRange(errors, draft, 'first_down_line', 'First-down line', 1, 99);
  const los = Number(draft.line_of_scrimmage);
  const marker = Number(draft.first_down_line);
  if (
    errors.first_down_line === undefined &&
    (draft.line_of_scrimmage ?? '') !== '' &&
    (draft.first_down_line ?? '') !== '' &&
    marker < los
  ) {
    errors.first_down_line = 'First-down line must be at or beyond the line of scrimmage.';
  }
  if (phase === 'special_teams') {
    checkRange(errors, draft, 'kick_distance', 'Kick distance', 0, 99);
    checkRange(errors, draft, 'return_yards', 'Return yards', -20, 109);
  }
  const allowedTypes = PLAY_TYPES_BY_PHASE[phase].map((option) => option.value);
  if (playType === '') errors.play_type = 'Play type is required.';
  else if (!allowedTypes.includes(playType)) {
    errors.play_type = `That play type is not logged on ${PHASE_LABELS[phase]}.`;
  }
  const outcome = draft.play_outcome ?? '';
  // A legacy `penalty` outcome stays valid and editable on old plays.
  if (outcome !== '' && !isAcceptedOutcome(playType, outcome)) {
    errors.play_outcome = 'That outcome cannot happen on this play type.';
  }
  const specialty = listOf(draft.specialty_outcome ?? '');
  for (const [left, right] of ILLEGAL_SPECIALTY_PAIRS) {
    if (specialty.includes(left) && specialty.includes(right)) {
      errors.specialty_outcome =
        left === 'fumble' && right === 'fumble_recovered'
          ? 'Fumble (Turnover) and Fumble recovered are mutually exclusive: pick one.'
          : 'Those tags cannot both be true on one play.';
    }
  }
  if (isDefensiveTouchdownWithoutCause(specialty)) {
    errors.specialty_outcome = DEFENSIVE_TD_CAUSE_MESSAGE;
  }
  if ((draft.notes ?? '').length > FIELD_BOUNDS.notesMaxLength) {
    errors.notes = `Notes must be ${FIELD_BOUNDS.notesMaxLength} characters or fewer.`;
  }
  if (
    listOf(draft.tags ?? '').length + listOf(draft.penalty_tags ?? '').length >
    FIELD_BOUNDS.tagsMaxCount
  ) {
    errors.tags = `Use at most ${FIELD_BOUNDS.tagsMaxCount} tags.`;
  }
  return errors;
}
